import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { ensureUserProfile } from "../../../Redux/Apis/authApi";
import { checkPermission, permissionType, hasAttendanceModuleAccess, canReadLeaveManagement, canPostTeamPraise, canReadProjectDocumentation, hasAdminAccess, canReadSops, canReadOffboardingDocuments, canAccessProjectCredentialsPage, canReadSalarySlips, canViewOwnSalarySlipsNav, canReadPerformanceCriteria, canReadTimeTracker, canReadTimeTrackerAdmin } from "./Functions";
import Loader from "../../../components/atoms/Loader/Loader";

const WithAuth = (WrappedComponent) => {
  return function AuthComponent(props) {
    const token = localStorage.getItem("accessToken");
    const {
      isAuth,
      user,
      loading: isLoading,
    } = useSelector((state) => state.auth.auth);

    const [loading, setLoading] = useState(true);
    const profileFetchStarted = useRef(false);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const location = useLocation();
    const pathName = location.pathname;
    const validatePath = (pathName) => {
      const pathParts = pathName.split("/").filter(Boolean);
      let key = pathParts.length > 0 && pathParts[0] ? pathParts[0] : "/";
      const permissionsReady = Array.isArray(user?.permissions);
      if (permissionsReady && hasAdminAccess(user)) {
        if (key === "leave_request") return false;
        return true;
      }

      switch (key) {
        case "dashboard":
          return true;
        case "employee-directory":
          return true;
        case "employees":
          if (!permissionsReady) return true;
          return checkPermission(permissionType.USER_MANAGEMENT, "read");
        case "daily-hours":
          if (!permissionsReady) return true;
          return checkPermission(permissionType.DAILY_HOURS, "read");
        case "assigned-projects":
          if (!permissionsReady) return true;
          return checkPermission(permissionType.PROJECT_MANAGEMENT, "read");
        case "project-management":
          if (!permissionsReady) return true;
          return checkPermission(permissionType.PROJECT_MANAGEMENT, "read");
        case "project-documentation":
          if (!permissionsReady) return true;
          return canReadProjectDocumentation();
        case "company-details":
          if (!permissionsReady) return true;
          return checkPermission(permissionType.COMPANY_DETAILS, "read");
        case "client-management":
          if (!permissionsReady) return true;
          return checkPermission(permissionType.CLIENT_MANAGEMENT, "read");
        case "invoice-management":
          if (!permissionsReady) return true;
          return checkPermission(permissionType.INVOICE_MANAGEMENT, "read");
        case "finance-management":
          if (!permissionsReady) return true;
          return (
            checkPermission(permissionType.FINANCE_MANAGEMENT, "read") ||
            checkPermission(permissionType.FINANCE_DOCUMENTS, "read") ||
            checkPermission(permissionType.FINANCE_EXPENSES, "read") ||
            checkPermission(permissionType.FINANCE_EXPENSES, "office_read")
          );
        case "salary-slips":
          if (!permissionsReady) return true;
          return canReadSalarySlips(user);
        case "my-salary-slips":
          if (!permissionsReady) return true;
          return canViewOwnSalarySlipsNav(user);
        case "company-ops":
          if (!permissionsReady) return true;
          return checkPermission(permissionType.COMPANY_OPS, "read");
        case "leave_request":
          if (!permissionsReady) return true;
          return canReadLeaveManagement();
        case "leave_approve":
          if (!permissionsReady) return true;
          return checkPermission(permissionType.LEAVE_APPROVE, "read");
        case "roles":
          if (!permissionsReady) return true;
          return checkPermission(permissionType.ROLE_MANAGEMENT, "read");
        case "sops":
          if (!permissionsReady) return true;
          return canReadSops();
        case "offboarding-documents":
          if (!permissionsReady) return true;
          return canReadOffboardingDocuments();
        case "project-credentials":
          if (!permissionsReady) return true;
          return canAccessProjectCredentialsPage(user);
        case "profile":
          return true;
        case "task_filter":
          if (!permissionsReady) return true;
          return (
            checkPermission(permissionType.DAILY_HOURS, "read") ||
            checkPermission(permissionType.DAILY_HOURS, "read users")
          );
        case "attendance":
          if (!permissionsReady) return true;
          return hasAttendanceModuleAccess();
        case "performance-criteria":
          if (!permissionsReady) return true;
          return canReadPerformanceCriteria();
        case "time-tracker":
          if (!permissionsReady) return true;
          if (pathParts[1] === "admin") return canReadTimeTrackerAdmin(user);
          return canReadTimeTracker();
        case "team-praise":
          if (!permissionsReady) return true;
          return canPostTeamPraise(user);
        case "company-policy":
          if (!permissionsReady) return true;
          return checkPermission(permissionType.COMPANY_POLICY, "read");
        default:
          return false;
      }
    };

    useEffect(() => {
      if (!token) {
        navigate("/login");
        return;
      }
      if (isAuth && user) {
        if (!validatePath(pathName)) {
          navigate("/not-found");
        }
        setLoading(false);
        return;
      }
      if (profileFetchStarted.current) {
        setLoading(true);
        return;
      }
      profileFetchStarted.current = true;
      setLoading(true);
      ensureUserProfile(dispatch).finally(() => {
        setLoading(false);
      });
    }, [token, isAuth, user, pathName, dispatch, navigate]);

    if (loading || (isLoading && !user)) {
      return (
        <div className="h-[100dvh] w-full flex item-center justify-center">
          <Loader />
        </div>
      );
    }

    if (!isAuth) {
      return <Navigate to="/login" replace />;
    }
    return <WrappedComponent {...props} />;
  };
};

export default WithAuth;
